import { useState } from "react";
import { useMutation, useQuery } from "react-query";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Button, Card, Form, Spinner } from "react-bootstrap";
import { useForm } from "react-hook-form";
import { AxiosError, AxiosResponse } from "axios";
import Select from "react-select";
import Icon from "@mdi/react";
import { mdiOfficeBuildingOutline, mdiChevronLeft } from "@mdi/js";

import { ModuleEntity, ModulePermissionEntity } from "../../config/defines";
import { modulePermissionCheck } from "../../config/utils";
import { useToast } from "../../context/ToastContext";
import { useAuth } from "../../context/AuthContext";
import { CompanyPostRequest, CompanyPostResponse, CompanyPutRequest, CompanyPutResponse } from "../../entities/CompanyEntity";
import CompanyService from "../../services/CompanyService";

import Layout from "../../components/Layout";
import QuestionnaireService from "../../services/QuestionnaireService";
import { QuestionnaireGetQuestionnairesResponse } from "../../entities/QuestionnaireEntity";
import { SelectOptions, SelectStyle } from "../../config/select";

const toastTitle = "Empresa";

export default function EmpresaFormulario() {
	const companyService = new CompanyService();
	const questionnaireService = new QuestionnaireService();

	const navigate = useNavigate();
	const { user } = useAuth();
	const { id } = useParams();
	const { handleToast } = useToast();

	const [formStatus, setFormStatus] = useState(id ? false : true);
	const [formSaving, setFormSaving] = useState(false);

	const queryQuestionnaires = useQuery<QuestionnaireGetQuestionnairesResponse[]>(["avaliacoes"], () => fetchDataQuestionnaires());

	const { data, isLoading, isFetching, isRefetching } = useQuery(["empresa", id], () => fetchData(id), { enabled: !!id && !formStatus });

	const { register, handleSubmit, reset, setValue } = useForm({ defaultValues: data ? { ...data, sendInvite: false } : undefined });

	const mutationPost = useMutation(mutatePost, { onSuccess: mutatePostSuccess, onError: mutateError });
	const mutationPut = useMutation(mutatePut, { onSuccess: mutatePutSuccess, onError: mutateError });

	async function fetchData(id: any) {
		let resp = await companyService.get(id);
		reset(resp.data);
		return resp.data;
	}

	async function fetchDataQuestionnaires() {
		const resp = await questionnaireService.getQuestionnaires();
		return resp.data;
	}

	async function mutatePost(data: CompanyPostRequest) {
		setFormSaving(true);
		return await companyService.post(data);
	}

	function mutatePostSuccess(resp: AxiosResponse<CompanyPostResponse, any>) {
		handleToast(toastTitle, "Informações salvas com sucesso!", 5000);
		navigate("/empresaFormulario/" + resp.data.id);
		setFormStatus(false);
		setFormSaving(false);
	}

	async function mutatePut(data: CompanyPutRequest) {
		setFormSaving(true);
		return await companyService.put(Number(id), data);
	}

	function mutatePutSuccess(resp: AxiosResponse<CompanyPutResponse, any>) {
		handleToast(toastTitle, "Informações salvas com sucesso!", 5000);
		setFormStatus(false);
		setFormSaving(false);
	}

	function mutateError(resp: AxiosError) {
		handleToast(toastTitle, `Problema na operação: ${resp.message}`, 5000, "danger");
		setFormSaving(false);
	}

	async function handleAvaliacoes(value: any) {
		setValue(
			`questionnaires`,
			value
				? value.map((item: any) => {
						return item.value;
				  })
				: undefined
		);
	}

	const formAvaliacoesOptions = queryQuestionnaires.data?.map((item) => {
		return { label: `${item.title}`, value: item.id };
	});

	return (
		<Layout>
			<Form
				onSubmit={handleSubmit((values) => {
					if (id) {
						mutationPut.mutate({
							name: values.name,
							questionnaires: values.questionnaires,
						});
					} else {
						mutationPost.mutate({
							name: values.name,
							adminEmail: values.adminEmail,
							questionnaires: values.questionnaires,
						});
					}
				})}
			>
				<h5 className="mt-4 mb-4 d-flex align-items-center fw-light">
					<Link to={"/empresas"} className="d-flex align-items-center text-decoration-none">
						<Icon path={mdiChevronLeft} size={1} className="me-1" /> <Icon path={mdiOfficeBuildingOutline} size={1} className="me-1" /> Formulário de Empresa
					</Link>
					{(isLoading || isFetching || isRefetching) && <Spinner size="sm" className="ms-1" variant="secondary" />}
					<div className="d-flex gap-2 float-right ms-auto" style={{ marginTop: -10, marginBottom: -10 }}>
						{modulePermissionCheck(user!, ModuleEntity.acessos, ModulePermissionEntity.editar) && (
							<>
								{!formStatus && (
									<Button
										variant="light"
										className="rounded-3 shadow-sm"
										onClick={() => {
											setFormStatus(true);
										}}
									>
										Editar Informações
									</Button>
								)}
								{formStatus && (
									<>
										<Button
											variant="light"
											className="rounded-3 shadow-sm"
											type="button"
											onClick={() => {
												if (id) {
													setFormStatus(false);
												} else {
													navigate("/empresas");
												}
											}}
											disabled={formSaving}
										>
											Cancelar
										</Button>
										<Button variant="primary" className="rounded-3 shadow-sm" type="submit" disabled={formSaving}>
											{formSaving ? (
												<>
													<Spinner animation="border" size="sm" className="me-2" /> Salvando
												</>
											) : (
												"Salvar Informações"
											)}
										</Button>
									</>
								)}
							</>
						)}
					</div>
				</h5>

				<Card className="mb-3">
					<Card.Body>
						<Form.Group className="mb-3" controlId="nome">
							<Form.Label>Nome</Form.Label>
							<Form.Control type="text" placeholder="Informe aqui" {...register("name", { required: true })} disabled={!formStatus} />
						</Form.Group>
						{id === undefined && (
							<Form.Group className="mb-3" controlId="email">
								<Form.Label>E-mail</Form.Label>
								<Form.Control type="email" placeholder="Informe aqui" {...register("adminEmail", { required: true })} disabled={!formStatus} />
							</Form.Group>
						)}
						<Form.Group className="mb-3" controlId={`type`}>
							<Form.Label>Avaliações</Form.Label>
							{(!queryQuestionnaires.isFetched || isLoading) && (
								<div className="form-control disabled">
									<Spinner size="sm" />
								</div>
							)}
							{queryQuestionnaires.isFetched && !isLoading && (
								<Select
									placeholder={"Selecione"}
									defaultValue={formAvaliacoesOptions?.filter((option) => {
										return data?.questionnaires?.some((questionnaire) => {
											return questionnaire === option.value;
										});
									})}
									options={formAvaliacoesOptions}
									onChange={(value) => {
										handleAvaliacoes(value);
									}}
									styles={SelectStyle}
									isMulti={true}
									isDisabled={!formStatus}
									{...SelectOptions}
								/>
							)}
						</Form.Group>
					</Card.Body>
				</Card>

				{modulePermissionCheck(user!, ModuleEntity.acessos, ModulePermissionEntity.editar) && (
					<div className="d-flex gap-2 mb-4">
						{!formStatus && (
							<Button
								variant="light"
								className="rounded-3 shadow-sm"
								onClick={() => {
									setFormStatus(true);
								}}
							>
								Editar Informações
							</Button>
						)}
						{formStatus && (
							<>
								<Button variant="primary" className="rounded-3 shadow-sm" type="submit" disabled={formSaving}>
									{formSaving ? (
										<>
											<Spinner animation="border" size="sm" className="me-2" /> Salvando
										</>
									) : (
										"Salvar Informações"
									)}
								</Button>
								<Button
									variant="light"
									className="rounded-3 shadow-sm"
									type="button"
									onClick={() => {
										if (id) {
											setFormStatus(false);
										} else {
											navigate("/empresas");
										}
									}}
									disabled={formSaving}
								>
									Cancelar
								</Button>
							</>
						)}
					</div>
				)}
			</Form>
		</Layout>
	);
}
